import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Truck, Plus, Edit, Power, PowerOff, DollarSign, Package, Navigation, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import './Entregadores.css';

const Entregadores = () => {
  const [modalAberto, setModalAberto] = useState(false);
  const [entregadorEditando, setEntregadorEditando] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    nome: '',
    telefone: '',
    senha: '',
    cnh: '',
    placaVeiculo: '',
    tipoVeiculo: 'moto',
    ativo: true,
    configuracao: {
      valorDiaria: 0,
      valorPorKm: 0,
      comissaoPorEntrega: 0,
      comissaoPercentual: 0
    }
  });
  const queryClient = useQueryClient();

  // Buscar estabelecimento
  const { data: estabelecimento } = useQuery(
    'estabelecimento-entregadores',
    async () => {
      try {
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        return response.data.data.estabelecimento;
      } catch (error) {
        if (error.response?.status === 404) {
          const response = await api.get('/api/estabelecimentos/padrao');
          return response.data.data.estabelecimento;
        }
        throw error;
      }
    }
  );

  // Buscar usuários do tipo entregador
  const { data: usuariosEntregadores } = useQuery(
    'usuarios-entregadores',
    async () => {
      const response = await api.get('/api/auth/users?tipo=entregador');
      return response.data.data?.users || [];
    },
    {
      enabled: false // Não buscar automaticamente
    }
  );

  // Buscar entregadores
  const { data: entregadores, isLoading } = useQuery(
    ['entregadores', estabelecimento?._id],
    async () => {
      if (!estabelecimento?._id) return [];
      const response = await api.get(`/api/entregadores?estabelecimento=${estabelecimento._id}`);
      return response.data.data.entregadores || [];
    },
    {
      enabled: !!estabelecimento?._id
    }
  );

  // Mutation para criar/atualizar
  const saveMutation = useMutation(
    (data) => {
      if (entregadorEditando) {
        return api.put(`/api/entregadores/${entregadorEditando._id}`, data);
      }
      return api.post('/api/entregadores', data);
    },
    {
      onSuccess: () => {
        toast.success(entregadorEditando ? 'Entregador atualizado!' : 'Entregador criado!');
        setModalAberto(false);
        setEntregadorEditando(null);
        resetForm();
        queryClient.invalidateQueries('entregadores');
      },
      onError: (error) => {
        console.error('Erro ao salvar entregador:', error.response?.data);
        const errorMessage = error.response?.data?.message || 
                            error.response?.data?.errors?.[0]?.msg || 
                            error.message || 
                            'Erro ao salvar entregador';
        toast.error(errorMessage);
      }
    }
  );

  const resetForm = () => {
    setFormData({
      email: '',
      nome: '',
      telefone: '',
      senha: '',
      cnh: '',
      placaVeiculo: '',
      tipoVeiculo: 'moto',
      ativo: true,
      configuracao: {
        valorDiaria: 0,
        valorPorKm: 0,
        comissaoPorEntrega: 0,
        comissaoPercentual: 0
      }
    });
  };

  const handleAbrirModal = (entregador = null) => {
    if (entregador) {
      setEntregadorEditando(entregador);
      setFormData({
        email: entregador.usuario?.email || '',
        nome: entregador.usuario?.nome || '',
        telefone: entregador.usuario?.telefone || '',
        senha: '', // Não preencher senha ao editar
        cnh: entregador.cnh || '',
        placaVeiculo: entregador.placaVeiculo || '',
        tipoVeiculo: entregador.tipoVeiculo || 'moto',
        ativo: entregador.ativo !== false,
        configuracao: entregador.configuracao || {
          valorDiaria: 0,
          valorPorKm: 0,
          comissaoPorEntrega: 0,
          comissaoPercentual: 0
        }
      });
    } else {
      setEntregadorEditando(null);
      resetForm();
    }
    setModalAberto(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }
    
    // Validar campos obrigatórios se estiver criando novo entregador
    if (!entregadorEditando) {
      if (!formData.email) {
        toast.error('Email é obrigatório');
        return;
      }
      if (!formData.nome) {
        toast.error('Nome é obrigatório');
        return;
      }
      if (!formData.senha) {
        toast.error('Senha é obrigatória para criar novo entregador');
        return;
      }
    }
    
    const dataToSend = {
      ...formData,
      estabelecimento: estabelecimento._id
    };
    
    // Se estiver editando, não enviar email
    if (entregadorEditando) {
      delete dataToSend.email;
    }
    
    // Garantir que configuracao está presente
    if (!dataToSend.configuracao) {
      dataToSend.configuracao = {
        valorDiaria: 0,
        valorPorKm: 0,
        comissaoPorEntrega: 0,
        comissaoPercentual: 0
      };
    }
    
    saveMutation.mutate(dataToSend);
  };

  if (isLoading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="entregadores-page">
      <div className="page-header">
        <div>
          <h2><Truck size={28} /> Entregadores</h2>
          <p>Gerencie os entregadores do estabelecimento</p>
        </div>
        <button className="btn btn-primary" onClick={() => handleAbrirModal()}>
          <Plus size={20} />
          Novo Entregador
        </button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="entregadores-stats">
        <div className="stat-card">
          <div className="stat-icon blue">
            <Truck size={24} />
          </div>
          <div className="stat-info">
            <h3>{entregadores?.length || 0}</h3>
            <p>Total de Entregadores</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green">
            <Power size={24} />
          </div>
          <div className="stat-info">
            <h3>{entregadores?.filter(e => e.online).length || 0}</h3>
            <p>Online Agora</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple">
            <Package size={24} />
          </div>
          <div className="stat-info">
            <h3>{entregadores?.reduce((acc, e) => acc + (e.totalEntregas || 0), 0) || 0}</h3>
            <p>Total de Entregas</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange">
            <DollarSign size={24} />
          </div>
          <div className="stat-info">
            <h3>R$ {entregadores?.reduce((acc, e) => acc + (e.totalGanhos || 0), 0).toFixed(2) || '0.00'}</h3>
            <p>Total em Comissões</p>
          </div>
        </div>
      </div>

      {/* Lista de Entregadores */}
      <div className="card">
        <table className="entregadores-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Telefone</th>
              <th>Placa</th>
              <th>Status</th>
              <th>Entregas</th>
              <th>Comissão</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {entregadores?.map((entregador) => (
              <tr key={entregador._id}>
                <td>
                  <strong>{entregador.usuario?.nome || 'N/A'}</strong>
                </td>
                <td>{entregador.usuario?.telefone || 'N/A'}</td>
                <td>{entregador.placaVeiculo || 'N/A'}</td>
                <td>
                  <span className={`status-badge ${entregador.online ? 'online' : 'offline'}`}>
                    {entregador.online ? '🟢 Online' : '⚫ Offline'}
                  </span>
                  {!entregador.ativo && (
                    <span className="status-badge inactive">Inativo</span>
                  )}
                </td>
                <td>{entregador.totalEntregas || 0}</td>
                <td>R$ {(entregador.totalGanhos || 0).toFixed(2)}</td>
                <td>
                  <div className="acoes">
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleAbrirModal(entregador)}
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!entregadores || entregadores.length === 0) && (
          <div className="empty-state">
            <Truck size={64} />
            <p>Nenhum entregador cadastrado</p>
            <button className="btn btn-primary" onClick={() => handleAbrirModal()}>
              <Plus size={20} />
              Cadastrar Primeiro Entregador
            </button>
          </div>
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      {modalAberto && (
        <div className="modal-overlay" onClick={() => setModalAberto(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{entregadorEditando ? 'Editar Entregador' : 'Novo Entregador'}</h3>
              <button className="modal-close" onClick={() => setModalAberto(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              {!entregadorEditando && (
                <>
                  <div className="form-group">
                    <label>Nome Completo *</label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Nome do entregador"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@exemplo.com"
                      required
                    />
                    <small>Se o email já existir, será usado o usuário existente. Caso contrário, será criado automaticamente.</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Telefone</label>
                      <input
                        type="text"
                        value={formData.telefone}
                        onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div className="form-group">
                      <label>Senha *</label>
                      <input
                        type="password"
                        value={formData.senha}
                        onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                        placeholder="Senha para login"
                        required
                      />
                      <small>Obrigatória apenas para novos entregadores</small>
                    </div>
                  </div>
                </>
              )}

              {entregadorEditando && (
                <div className="form-group">
                  <label>Usuário</label>
                  <input
                    type="text"
                    value={`${entregadorEditando.usuario?.nome || ''} (${entregadorEditando.usuario?.email || ''})`}
                    disabled
                    className="input-disabled"
                  />
                  <small>Dados do usuário não podem ser alterados aqui</small>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>CNH</label>
                  <input
                    type="text"
                    value={formData.cnh}
                    onChange={(e) => setFormData({ ...formData, cnh: e.target.value })}
                    placeholder="00000000000"
                  />
                </div>
                <div className="form-group">
                  <label>Placa do Veículo</label>
                  <input
                    type="text"
                    value={formData.placaVeiculo}
                    onChange={(e) => setFormData({ ...formData, placaVeiculo: e.target.value.toUpperCase() })}
                    placeholder="ABC1234"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Tipo de Veículo</label>
                <select
                  value={formData.tipoVeiculo}
                  onChange={(e) => setFormData({ ...formData, tipoVeiculo: e.target.value })}
                >
                  <option value="moto">Moto</option>
                  <option value="bicicleta">Bicicleta</option>
                  <option value="carro">Carro</option>
                </select>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.ativo}
                    onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  />
                  Ativo
                </label>
              </div>

              <h4 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Configurações Financeiras</h4>

              <div className="form-row">
                <div className="form-group">
                  <label>Valor da Diária (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.configuracao.valorDiaria}
                    onChange={(e) => setFormData({
                      ...formData,
                      configuracao: { ...formData.configuracao, valorDiaria: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Valor por KM (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.configuracao.valorPorKm}
                    onChange={(e) => setFormData({
                      ...formData,
                      configuracao: { ...formData.configuracao, valorPorKm: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Comissão por Entrega (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.configuracao.comissaoPorEntrega}
                    onChange={(e) => setFormData({
                      ...formData,
                      configuracao: { ...formData.configuracao, comissaoPorEntrega: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div className="form-group">
                  <label>Comissão Percentual (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.configuracao.comissaoPercentual}
                    onChange={(e) => setFormData({
                      ...formData,
                      configuracao: { ...formData.configuracao, comissaoPercentual: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModalAberto(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saveMutation.isLoading}>
                  {saveMutation.isLoading ? 'Salvando...' : entregadorEditando ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Entregadores;

